import { useTranslation } from 'react-i18next'
import { Check, Loader2, X } from 'lucide-react'
import type { ImportActionModuleScoresRequest } from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'

interface Props {
  open: boolean
  rows: ImportActionModuleScoresRequest[]
  isUploading: boolean
  onConfirm: () => void
  onCancel: () => void
}

const EMPTY_CELL = '—'

/** The reward value cell: number for Score/ManualInput, a check/cross for Binary. */
function ValueCell({ info }: { info: ImportActionModuleScoresRequest['rewardCalculationInformation'] }) {
  if (!info) return <>{EMPTY_CELL}</>
  switch (info.incentivationDataType) {
    case IncentivationDataType.Score:
      return <>{(info as any).score ?? EMPTY_CELL}</>
    case IncentivationDataType.ManualInput:
      return <>{(info as any).enteredReward ?? EMPTY_CELL}</>
    case IncentivationDataType.Binary:
      return (info as any).isFulfilled
        ? <Check className="size-4 text-green-600" />
        : <X className="size-4 text-destructive" />
    default:
      return <>{EMPTY_CELL}</>
  }
}

/**
 * Read-only confirmation of the parsed import rows (employee number + reward value +
 * any subtype columns) shown before the POST. Mirrors EmployeeImportPreviewDialog.
 */
export function ImportPreviewDialog({ open, rows, isUploading, onConfirm, onCancel }: Props) {
  const { t } = useTranslation(['action-modules-detail', 'common'])

  // Union of subtype-data keys across all rows → dynamic columns.
  const subtypeCols = [...new Set(rows.flatMap((r) => (r.subTypeData ? Object.keys(r.subTypeData) : [])))]

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isUploading && onCancel()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('importExport.preview.title')}</DialogTitle>
          <DialogDescription>{t('importExport.preview.description', { count: rows.length })}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto rounded-md border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead>{t('importExport.preview.employeeNumber')}</TableHead>
                <TableHead>{t('importExport.preview.value')}</TableHead>
                {subtypeCols.map((col) => <TableHead key={col}>{col}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={`${row.employeeNumber}-${i}`}>
                  <TableCell>{row.employeeNumber || EMPTY_CELL}</TableCell>
                  <TableCell><ValueCell info={row.rewardCalculationInformation} /></TableCell>
                  {subtypeCols.map((col) => (
                    <TableCell key={col}>{row.subTypeData?.[col] ?? EMPTY_CELL}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isUploading}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={onConfirm} disabled={isUploading}>
            {isUploading && <Loader2 className="size-4 animate-spin" />}
            {t('importExport.preview.upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
